// @ts-strict-ignore
import React from 'react';
import {
  Box, Icon, IconName, Text,
} from '@usealloy/component-library';
import type { Permissions } from '~/helpers/typescript';
import {
  getIncomingDataRoutingCategories,
  getOutgoingDataRoutingCategories,
} from 'flow/service/dataRoutes';
import type { FormState } from './useNodeEditorForm';
import { getActionTabKey } from './useNodeEditorForm';
import type {
  Action, NodeEditorTab, SchemaInstance, WorkflowProps,
} from 'flow/store/engine/types';
import type { NodeEditorDrawerTabConfig } from './types';
import { ActionType } from 'flow/store/engine/constants';
import {
  isActionOfType,
  getActionTypeText,
  actionHasDisplayName,
  getActionName,
} from 'flow/service/actions';
import { StyledCounter, StyledDependentsLabel } from './DependentsTab/styles';
import AttributeConfigurationFooter from './AttributeConfiguration/Footer';
import ServiceDataRoutingTab from './ServiceTabs/ServiceDataRoutingTab';
import DependentsTab from './DependentsTab';
import InputAttributeConfigurationTab from './AttributeConfiguration/InputAttributeConfigurationTab';
import LinkedChildActionFooter from './ThresholdsTab/LinkedChildActionFooter';
import MatrixOptionsTab from './MatrixOptionsTab';
import OutputAttributeExpressionTab, {
  Footer as OutputAttributeExpressionFooter,
} from './OutputAttributeExpressionTab';
import OutputAttributeOptionsTab from './AttributeConfiguration/OutputAttributeOptionsTab';
import ServiceLocalSettingsTab, {
  Footer as ServiceLocalSettingsFooter,
} from './ServiceTabs/ServiceLocalSettingsTab';
import TagGlobalSettingsTab, { Footer as TagGlobalSettingsFooter } from './TagGlobalSettingsTab';
import TagLocalSettingsTab, { Footer as TagLocalSettingsFooter } from './TagLocalSettingsTab';
import ThresholdsTab from './ThresholdsTab';
import NodeTooltip from '../Graph/nodes/BaseNode/NodeTooltip';
import type { UserContextValue } from '~/dashboard/context';
import { isSnippetChildAction } from 'flow/service/actions/snippets';
import { DiffViewSplit } from '../DiffViewSplit/DiffViewSplit';

export function getDrawerTitleText(focusedAction: Action): React.ReactNode {
  if (!focusedAction || focusedAction.type === ActionType.PREVIEW) return null;

  const label = isActionOfType(focusedAction, ActionType.SERVICE)
    ? focusedAction.service.name
    : getActionTypeText(focusedAction);
  const isServiceWithoutDisplayName = isActionOfType(focusedAction, ActionType.SERVICE)
  && !actionHasDisplayName(focusedAction);
  return (
    <>
      <Text as="h3" size={3} style={{ color: 'var(--cl-gray-600)', marginRight: '5px' }}>
        {isServiceWithoutDisplayName ? label : `${label}:`}
      </Text>
      {!isServiceWithoutDisplayName && getActionName(focusedAction)}
    </>
  );
}

function generateIcon(iconName: IconName, type: ActionType) {
  return (
    <Box gap={2} data-dd-action-name={`drawer-header-icon-${type}`}>
      <Icon color="var(--cl-gray-600)" icon={iconName} />
    </Box>
  );
}

export function getDrawerTitleIcon(focusedAction: Action) {
  if (!focusedAction) {
    return null;
  }
  switch (focusedAction.type) {
    case ActionType.INPUT:
      return generateIcon(IconName.Input, focusedAction.type);
    case ActionType.MATRIX_MODEL:
    case ActionType.IMPORTED_MATRIX_MODEL:
      return generateIcon(IconName.Matrix, focusedAction.type);
    case ActionType.OUTPUT:
    case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
      return generateIcon(IconName.Output, focusedAction.type);
    case ActionType.SERVICE:
      return generateIcon(IconName.Service, focusedAction.type);
    case ActionType.TAG:
      return generateIcon(IconName.Tag, focusedAction.type);
    case ActionType.OUTCOME_REASON:
      return generateIcon(IconName.Stamp, focusedAction.type);
    default:
      return null;
  }
}

function renderTabContent(content: React.ReactNode, shouldBeSplit: boolean) {
  return shouldBeSplit ? <DiffViewSplit showBadges>{content}</DiffViewSplit> : content;
}

function createOptionLabel(label: string) {
  const dataName = label.replace(' ', '-').toLowerCase();
  return <div data-dd-action-name={`drawer-tab-${dataName}`}>{label}</div>;
}

export function getDrawerTabs(args: {
  dataRoutes: SchemaInstance['dataRoutes'];
  permissions: Partial<Permissions>;
  focusedAction: Action;
  options: {
    isCreatingLinkedChildAction: boolean;
    numDependents: number;
  };
  services: WorkflowProps['services'];
  inEditMode: boolean;
  isInDiffMode: boolean;
  focusedActionHasInsertionError?: boolean;
  breadcrumbs: UserContextValue['breadcrumbs'];
  handleFormAutoSave(formState: FormState): void;
  isDynamicDataRoutingEnabled?: boolean;
  shouldBeSplit: boolean;
}): NodeEditorDrawerTabConfig[] {
  const {
    dataRoutes,
    permissions,
    focusedAction,
    handleFormAutoSave,
    options: { isCreatingLinkedChildAction, numDependents },
    services,
    inEditMode,
    isInDiffMode,
    shouldBeSplit,
    focusedActionHasInsertionError = false,
    isDynamicDataRoutingEnabled = false,
  } = args;

  if (!focusedAction) {
    return [];
  }

  const isReadOnly = isSnippetChildAction(focusedAction) || isInDiffMode;

  const DependentsLabel = (
    <StyledDependentsLabel data-dd-action-name="drawer-tab-dependents">
      Dependents
      <StyledCounter>{numDependents}</StyledCounter>
    </StyledDependentsLabel>
  );

  let dataRoutingTab: NodeEditorDrawerTabConfig[] = [];

  if (focusedAction.type === ActionType.SERVICE && inEditMode) {
    const availableIncomingCategories = getIncomingDataRoutingCategories({
      action: focusedAction,
      dataRoutes,
      services,
    });

    const availableOutgoingCategories = getOutgoingDataRoutingCategories({
      action: focusedAction,
      dataRoutes,
      services,
    });

    // Dynamic routes have no fixed category, so show the tab when enabled even with no legacy routes yet.
    if (
      availableIncomingCategories.length
      || availableOutgoingCategories.length
      || isDynamicDataRoutingEnabled
    ) {
      dataRoutingTab = [
        {
          label: createOptionLabel('Data Routing'),
          drawerContent: renderTabContent(<ServiceDataRoutingTab />, shouldBeSplit),
          value: 'datarouting',
        },
      ];
    }
  }

  let serviceLocalSettingsTab: NodeEditorDrawerTabConfig[] = [];
  if (
    permissions?.['READ:CustomerServices:null']
    && isActionOfType(focusedAction, ActionType.SERVICE)
  ) {
    const serviceLocalSettingsLabel = createOptionLabel('Local Settings');
    serviceLocalSettingsTab = [
      {
        label: serviceLocalSettingsLabel,
        drawerContent: renderTabContent(
          <ServiceLocalSettingsTab handleAutoSave={handleFormAutoSave} isReadOnly={isReadOnly} />,
          shouldBeSplit,
        ),
        value: 'configuration',
        id: 'workflow-editor-service-local-settings-drawer',
      },
    ];
  }

  const configurationLabel = createOptionLabel('Configuration');
  const optionsLabel = createOptionLabel('Options');
  const thresholdLabel = createOptionLabel('Thresholds');
  switch (focusedAction.type) {
    case ActionType.INPUT: {
      return [
        {
          drawerContent: renderTabContent(
            <InputAttributeConfigurationTab
              handleAutoSave={handleFormAutoSave}
              isReadOnly={isReadOnly}
            />,
            shouldBeSplit,
          ),
          label: configurationLabel,
          value: 'configuration' as NodeEditorTab,
        },
        {
          drawerContent: renderTabContent(
            <DependentsTab />,
            shouldBeSplit,
          ),
          label: DependentsLabel,
          value: 'dependents' as NodeEditorTab,
        },
      ];
    }
    case ActionType.OUTPUT: {
      return [
        {
          label: createOptionLabel('Expression'),
          drawerContent: renderTabContent(
            <OutputAttributeExpressionTab
              // To avoid circular import, passing NodeTooltip into ThresholdsTab
              ActionHoverTooltip={props => <NodeTooltip canClickToAction {...props} />}
            />,
            shouldBeSplit,
          ),
          value: 'rule',
        },
        {
          drawerContent: renderTabContent(
            <DependentsTab />,
            shouldBeSplit,
          ),
          label: DependentsLabel,
          value: 'dependents' as NodeEditorTab,
        },
        ...(inEditMode
          ? [
            {
              label: optionsLabel,
              drawerContent: renderTabContent(
                <OutputAttributeOptionsTab
                  handleAutoSave={handleFormAutoSave}
                  isReadOnly={isReadOnly}
                />,
                shouldBeSplit,
              ),
              value: 'options' as NodeEditorTab,
            },
          ]
          : []),
      ];
    }
    case ActionType.TAG: {
      const showTagGlobalSettings = inEditMode || focusedActionHasInsertionError;
      const isErrorCorrectionOnly = focusedActionHasInsertionError && !inEditMode;
      return [
        {
          label: thresholdLabel,
          drawerContent: renderTabContent(
            <ThresholdsTab
              // To avoid circular import, passing NodeTooltip into ThresholdsTab
              ActionHoverTooltip={props => <NodeTooltip canClickToAction {...props} />}
            />,
            shouldBeSplit,
          ),
          value: 'rule',
        },
        {
          label: DependentsLabel,
          drawerContent: renderTabContent(
            <DependentsTab />,
            shouldBeSplit,
          ),
          value: 'dependents',
        },
        ...(inEditMode
          ? [
            {
              label: createOptionLabel('Local Settings'),
              drawerContent: renderTabContent(
                <TagLocalSettingsTab
                  handleAutoSave={handleFormAutoSave}
                  isReadOnly={isReadOnly}
                />,
                shouldBeSplit,
              ),
              // using 'options' instead of creating new NodeEditorTab value
              value: 'options' as NodeEditorTab,
            },
          ]
          : []),
        ...(showTagGlobalSettings
          ? [
            {
              label: createOptionLabel('Global Settings'),
              drawerContent: renderTabContent(
                <TagGlobalSettingsTab
                  isReadOnly={isReadOnly}
                  isErrorCorrectionOnly={isErrorCorrectionOnly}
                />,
                shouldBeSplit,
              ),
              // using 'configuration' instead of creating new NodeEditorTab value
              value: 'configuration' as NodeEditorTab,
            },
          ]
          : []),
      ];
    }
    case ActionType.SERVICE:
      return [
        {
          label: thresholdLabel,
          drawerContent: renderTabContent(
            <ThresholdsTab
              // Pass in a falsey action value when in diff mode so focusedAction is re-selected
              action={isInDiffMode ? focusedAction : null}
              // To avoid circular import, passing NodeTooltip into ThresholdsTab
              ActionHoverTooltip={props => <NodeTooltip canClickToAction {...props} />}
            />,
            shouldBeSplit,
          ),
          value: 'rule',
        },
        {
          label: DependentsLabel,
          drawerContent: renderTabContent(
            <DependentsTab />,
            shouldBeSplit,
          ),
          value: 'dependents',
        },
        ...(inEditMode ? [...dataRoutingTab, ...serviceLocalSettingsTab] : []),
      ];
    case ActionType.PREVIEW:
      return [
        {
          label: thresholdLabel,
          drawerContent: renderTabContent(
            <ThresholdsTab
              // To avoid circular import, passing NodeTooltip into ThresholdsTab
              ActionHoverTooltip={props => <NodeTooltip canClickToAction {...props} />}
            />,
            shouldBeSplit,
          ),
          value: 'rule',
        },
      ];
    case ActionType.MATRIX_MODEL:
      return [
        {
          label: configurationLabel,
          drawerContent: renderTabContent(
            <ThresholdsTab
              // To avoid circular import, passing NodeTooltip into ThresholdsTab
              ActionHoverTooltip={props => <NodeTooltip canClickToAction {...props} />}
            />,
            shouldBeSplit,
          ),
          value: 'rule',
        },
        {
          label: DependentsLabel,
          drawerContent: renderTabContent(
            <DependentsTab />,
            shouldBeSplit,
          ),
          value: 'dependents',
        },
        ...(inEditMode
          ? [
            {
              label: optionsLabel,
              drawerContent: renderTabContent(
                <MatrixOptionsTab handleAutoSave={handleFormAutoSave} isReadOnly={isReadOnly} />,
                shouldBeSplit,
              ),
              value: 'options' as NodeEditorTab,
            },
          ]
          : []),
      ];
    default:
      return [
        {
          label: thresholdLabel,
          drawerContent: renderTabContent(
            <ThresholdsTab
              // To avoid circular import, passing NodeTooltip into ThresholdsTab
              ActionHoverTooltip={props => <NodeTooltip canClickToAction {...props} />}
            />,
            shouldBeSplit,
          ),
          value: 'rule',
        },
        {
          disabled: isCreatingLinkedChildAction,
          label: DependentsLabel,
          drawerContent: renderTabContent(
            <DependentsTab />,
            shouldBeSplit,
          ),
          value: 'dependents',
        },
      ];
  }
}

function SplitOutputAttributeExpressionFooter(props: Record<string, unknown>) {
  return (
    <DiffViewSplit showBadges={false}>
      <OutputAttributeExpressionFooter {...props} />
    </DiffViewSplit>
  );
}

export function getDrawerTabFooter(
  activeNodeEditorTab: NodeEditorTab,
  focusedAction: Action,
  isInDiffMode: boolean,
  shouldBeSplit: boolean,
) {
  if (!focusedAction) {
    return null;
  }

  const tabFooterMap = {
    [getActionTabKey(ActionType.PREVIEW, 'rule')]: LinkedChildActionFooter,
    // The expression footer is the one footer that stays visible in diff mode, and only for
    // changed actions, where it is rendered once per pane so each side keeps its own footer.
    ...(shouldBeSplit && {
      [getActionTabKey(ActionType.OUTPUT, 'rule')]: SplitOutputAttributeExpressionFooter,
    }),
    ...(!isInDiffMode && {
      [getActionTabKey(ActionType.OUTPUT, 'rule')]: OutputAttributeExpressionFooter,
      [getActionTabKey(ActionType.INPUT, 'configuration')]: AttributeConfigurationFooter,
      [getActionTabKey(ActionType.SERVICE, 'configuration')]: ServiceLocalSettingsFooter,
      [getActionTabKey(ActionType.TAG, 'options')]: TagLocalSettingsFooter,
      [getActionTabKey(ActionType.TAG, 'configuration')]: TagGlobalSettingsFooter,
      [getActionTabKey(ActionType.OUTPUT, 'options')]: AttributeConfigurationFooter,
    }),
    // add other tab footers here
  };

  const tabKey = getActionTabKey(focusedAction.type, activeNodeEditorTab);
  return tabFooterMap[tabKey];
}
